import { MapPin, Phone, Clock, ExternalLink } from "lucide-react";
import { BRANCHES, BRAND, telLink } from "@/lib/brand";
import { TESTIDS } from "@/constants/testIds";

export default function BranchesPreview({ full = false }) {
  return (
    <section
      data-testid={TESTIDS.branchesSection}
      id="branches"
      className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Our Branches · शाखाएं
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Two branches in Gurgaon.
              <br />
              <span className="italic text-[#641C24]">
                Home visits
              </span>{" "}
              across North India.
            </h2>
          </div>
          <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
            Walk into our nearest branch for an instant valuation, or call to
            schedule a home visit — {BRAND.cities.replace("Serving ", "")}.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {BRANCHES.map((b) => (
            <div
              key={b.id}
              data-testid={TESTIDS.branchCard(b.id)}
              className="bg-white border border-[#E5DCC5] shadow-sm overflow-hidden flex flex-col"
            >
              <iframe
                data-testid={TESTIDS.branchMap(b.id)}
                title={`Map — ${b.name}`}
                src={b.embed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-56 md:h-64 border-0"
              />
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">
                      {b.name}
                    </h3>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#641C24] mt-1">
                      {b.hindi}
                    </div>
                  </div>
                  <MapPin size={20} className="text-[#D4AF37] shrink-0" />
                </div>
                <div className="mt-4 space-y-2 text-sm text-[#4A3B3C]">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-0.5 shrink-0" />
                    {b.address}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="shrink-0" /> {b.hours}
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="shrink-0" /> {BRAND.phone}
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-2 pt-4 border-t border-[#E5DCC5]">
                  <a
                    data-testid={TESTIDS.branchDirections(b.id)}
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                      b.mapsQuery
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#641C24] hover:bg-[#4A151A] text-white px-5 py-3 text-xs font-semibold uppercase tracking-wider flex-1"
                  >
                    <ExternalLink size={13} /> Get Directions
                  </a>
                  <a
                    href={telLink}
                    className="inline-flex items-center justify-center gap-2 border border-[#D4AF37] hover:bg-[#D4AF37] text-[#2D181A] px-5 py-3 text-xs font-semibold uppercase tracking-wider"
                  >
                    <Phone size={13} /> Call Branch
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {full && (
          <div className="mt-14 bg-white border border-[#E5DCC5] p-8 md:p-10">
            <h3 className="font-display text-xl md:text-2xl font-bold">
              Home Visit Service
            </h3>
            <p className="text-sm text-[#4A3B3C] leading-relaxed mt-3 max-w-3xl">
              For larger quantities, senior citizens or if you're outside
              Gurgaon — we send a govt-approved valuer to your doorstep across
              Delhi NCR, Faridabad, Noida, Ghaziabad, Chandigarh, Jaipur,
              Lucknow and other North Indian cities. Karat testing and payment
              happen right at your home. Prior appointment required —{" "}
              <a
                href={telLink}
                className="text-[#641C24] font-semibold underline decoration-[#D4AF37] underline-offset-4"
              >
                call {BRAND.phone}
              </a>
              .
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
